import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BillingContactsPanel = ({ isReadOnly = false }) => {
  const [contacts, setContacts] = useState([
    {
      id: 1,
      name: 'Bonalizah Tio',
      email: 'admin@hrpacificrbs.com',
      phone: '+675 78808282',
      role: 'primary',
      emailNotifications: true,
      invoiceAlerts: true
    }
  ]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'secondary',
    emailNotifications: true,
    invoiceAlerts: true
  });

  const roleOptions = [
    { value: 'primary', label: 'Primary Contact', description: 'Main billing contact' },
    { value: 'secondary', label: 'Secondary Contact', description: 'Backup contact' },
    { value: 'finance', label: 'Finance Manager', description: 'Financial oversight' }
  ];

  const handleAddContact = () => {
    if (isReadOnly) return;
    if (formData?.name && formData?.email) {
      if (editingId) {
        setContacts(contacts?.map(contact =>
          contact?.id === editingId ? { ...formData, id: editingId } : contact
        ));
        setEditingId(null);
      } else {
        setContacts([...contacts, { ...formData, id: Date.now() }]);
      }
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: 'secondary',
        emailNotifications: true,
        invoiceAlerts: true
      });
      setShowAddForm(false);
    }
  };

  const handleEditContact = (contact) => {
    if (isReadOnly) return;
    setFormData(contact);
    setEditingId(contact?.id);
    setShowAddForm(true);
  };

  const handleDeleteContact = (id) => {
    if (isReadOnly) return;
    setContacts(contacts?.filter(contact => contact?.id !== id));
  };

  const handleToggleNotification = (id, field) => {
    if (isReadOnly) return;
    setContacts(contacts?.map(contact =>
      contact?.id === id ? { ...contact, [field]: !contact?.[field] } : contact
    ));
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      primary: 'bg-primary text-primary-foreground',
      secondary: 'bg-secondary text-secondary-foreground',
      finance: 'bg-accent text-accent-foreground'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Billing Contacts</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage billing notification recipients</p>
        </div>
        {!isReadOnly && (
          <Button
            variant="outline"
            size="sm"
            iconName="UserPlus"
            onClick={() => {
              setShowAddForm(!showAddForm);
              setEditingId(null);
              setFormData({
                name: '',
                email: '',
                phone: '',
                role: 'secondary',
                emailNotifications: true,
                invoiceAlerts: true
              });
            }}
          >
            Add Contact
          </Button>
        )}
      </div>

      {/* Add/Edit Contact Form */}
      {showAddForm && !isReadOnly && (
        <div className="bg-muted/30 border border-border rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            {editingId ? 'Edit Contact' : 'Add New Contact'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Full Name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              placeholder="John Doe"
              required
            />
            <Input
              label="Email Address"
              type="email"
              value={formData?.email}
              onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
              placeholder="john.doe@company.com"
              required
            />
            <Input
              label="Phone Number"
              type="tel"
              value={formData?.phone}
              onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
              placeholder="+675 7123 4567"
            />
            <Select
              label="Role"
              value={formData?.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              options={roleOptions}
            />
          </div>
          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.emailNotifications}
                onChange={(e) => setFormData({ ...formData, emailNotifications: e?.target?.checked })}
                className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-ring"
              />
              <span className="text-sm text-foreground">Email Notifications</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.invoiceAlerts}
                onChange={(e) => setFormData({ ...formData, invoiceAlerts: e?.target?.checked })}
                className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-ring"
              />
              <span className="text-sm text-foreground">Invoice Alerts</span>
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" onClick={handleAddContact}>
              {editingId ? 'Update Contact' : 'Save Contact'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowAddForm(false);
                setEditingId(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Contact List */}
      <div className="space-y-3">
        {contacts?.map((contact) => (
          <div
            key={contact?.id}
            className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-foreground">{contact?.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(contact?.role)}`}>
                      {contact?.role?.charAt(0)?.toUpperCase() + contact?.role?.slice(1)}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Mail" size={14} />
                      <span>{contact?.email}</span>
                    </div>
                    {contact?.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="Phone" size={14} />
                        <span>{contact?.phone}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      <Icon
                        name={contact?.emailNotifications ? 'Bell' : 'BellOff'}
                        size={14}
                        className={contact?.emailNotifications ? 'text-success' : 'text-muted-foreground'}
                      />
                      <span className="text-xs text-muted-foreground">Email</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon
                        name={contact?.invoiceAlerts ? 'FileText' : 'FileX'}
                        size={14}
                        className={contact?.invoiceAlerts ? 'text-success' : 'text-muted-foreground'}
                      />
                      <span className="text-xs text-muted-foreground">Invoices</span>
                    </div>
                  </div>
                </div>
              </div>
              {!isReadOnly && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    iconName="Edit"
                    onClick={() => handleEditContact(contact)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    iconName="Trash2"
                    onClick={() => handleDeleteContact(contact?.id)}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {contacts?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No billing contacts added yet</p>
        </div>
      )}
    </div>
  );
};

export default BillingContactsPanel;